import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, ArrowRight, ArrowLeft, AlertCircle, Loader2 } from "lucide-react";

export default function ValidationScreen({ 
  runId, 
  validation, 
  uploads, 
  onFetchValidation, 
  onProceed, 
  onBack,
  loading 
}) {
  const navigate = useNavigate();
  const [fetchingValidation, setFetchingValidation] = useState(false);

  useEffect(() => {
    if (!runId) {
      navigate("/");
      return;
    }
    fetchValidation();
  }, [runId]);

  const fetchValidation = async () => {
    setFetchingValidation(true);
    await onFetchValidation();
    setFetchingValidation(false);
  };

  const allValid = validation?.all_valid || false;
  const files = validation?.files || uploads || [];

  const hasBlockingErrors = files.some(f => !f.schema_valid);
  const hasWarnings = false; // Could add warning logic if needed

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-semibold tracking-tight text-[#062B55]" data-testid="validation-headline">
          Check data before processing
        </h1>
        <p className="text-slate-600 mt-2">
          Fix missing required columns.
          <br />
          <span className="text-slate-500">You can proceed with warnings.</span>
        </p>
      </div>

      {/* Validation Summary Card */}
      <Card className="card-surface mb-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#062B55]">Validation Summary</CardTitle>
            {fetchingValidation ? (
              <Loader2 size={20} className="text-slate-400 animate-spin" />
            ) : allValid ? (
              <span className="badge-match flex items-center gap-1">
                <Check size={14} />
                Ready to process
              </span>
            ) : (
              <span className="badge-mismatch flex items-center gap-1">
                <AlertCircle size={14} />
                Issues found
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {files.length === 0 ? (
            <div className="empty-state py-8">
              <p className="empty-state-title">No files uploaded yet</p>
              <p className="empty-state-text">Go back to upload your files</p>
            </div>
          ) : (
            <Table className="exception-table">
              <TableHeader>
                <TableRow>
                  <TableHead>File Type</TableHead>
                  <TableHead>File Name</TableHead>
                  <TableHead className="text-right">Rows</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.upload_id} data-testid={`validation-row-${file.file_type.toLowerCase()}`}>
                    <TableCell className="font-medium">{file.file_type}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-600">{file.file_name}</TableCell>
                    <TableCell className="text-right font-mono">{file.row_count}</TableCell>
                    <TableCell>
                      {file.schema_valid ? (
                        <span className="badge-match flex items-center gap-1 w-fit">
                          <Check size={12} />
                          Valid
                        </span>
                      ) : (
                        <span className="badge-mismatch flex items-center gap-1 w-fit">
                          <X size={12} />
                          Invalid
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {file.schema_valid ? (
                        <span className="text-sm text-slate-500">All required columns present</span>
                      ) : (
                        <span className="text-sm text-red-600" data-testid={`validation-error-${file.file_type.toLowerCase()}`}>
                          {file.blocking_error}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Required Columns Reference */}
      <Card className="card-surface mb-8">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700">Required Columns Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <span className="font-semibold text-[#062B55]">PO:</span>
              <code className="ml-2 text-slate-600 font-mono text-xs">
                po_number, vendor_id, amount, date
              </code>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <span className="font-semibold text-[#062B55]">GRN:</span>
              <code className="ml-2 text-slate-600 font-mono text-xs">
                grn_number, po_number, received_qty, date
              </code>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
              <span className="font-semibold text-[#062B55]">Invoice:</span>
              <code className="ml-2 text-slate-600 font-mono text-xs">
                invoice_number, po_number, amount, date
              </code>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {hasBlockingErrors && (
        <div className="flex items-center gap-2 text-red-600 mb-4" data-testid="validation-blocking-error">
          <AlertCircle size={18} />
          <span className="text-sm">One or more files are missing required columns.</span>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="flex items-center gap-2 text-slate-600 rounded-xl"
          data-testid="validation-back-button"
        >
          <ArrowLeft size={18} />
          Re-upload files
        </Button>
        <Button
          onClick={onProceed}
          disabled={!allValid || loading}
          className="btn-primary flex items-center gap-2"
          data-testid="validation-proceed-button"
        >
          Start processing
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );
}
